// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography from "@west/App/Components/TitleTypography/TitleTypography";
import XngCarousel, { SwiperStyle } from "@west/App/Components/XngCarousel";
import loadPopularCategories from "@west/App/Handlers/Categories/LoadPopularCategories";
import { CustomAppState } from "@west/App/Store/Reducers";
import { getPopularCategories } from "@west/App/Store/Selectors/CategoriesSelector";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    sectionTitle = "sectionTitle",
    selectedCategoryIds = "selectedCategoryIds",
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const categoriesData = getPopularCategories(state, ownProps.id);
    return {
        isLoading: categoriesData.isLoading,
        categories: categoriesData.data,
        categoryPageLink: getPageLinkByPageType(state, "CategoryListPage"),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        xGoogleAnalytics: (state as CustomAppState).xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    loadPopularCategories,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.selectedCategoryIds]: string[];
    };
}

interface CategoryCardStyles {
    container?: InjectableCss;
    titleLink?: LinkPresentationProps;
    image?: LazyImageProps;
    imageClickable?: ClickablePresentationProps;
}

interface ShopByCategoriesStyles {
    carouselSlidesContainer?: InjectableCss;
    categoryCard?: CategoryCardStyles;
    categoryCarousel?: SwiperStyle;
}

export const shopByCategoriesStyles: ShopByCategoriesStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = shopByCategoriesStyles;
const ShopByCategories: React.FC<Props> = ({
    id,
    fields,
    categoryPageLink,
    categories,
    isTabletScreen,
    loadPopularCategories,
    xGoogleAnalytics,
}) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    useEffect(() => {
        const selectedCategoryIds = fields.selectedCategoryIds;

        if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
            return;
        }

        loadPopularCategories({
            id,
            categories: selectedCategoryIds,
        });
    }, [fields.selectedCategoryIds]);

    if (!categories || categories.length === 0) {
        return null;
    }
    const onClick = (url?: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    // XNG-Changes
    const categoryClickHandler = (url?: string, categoryLabel?: string) => {
        if (xGoogleAnalytics.hpShopByCategoryEvent && url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.hpShopByCategoryEventName,
                click_text: categoryLabel,
                destination: url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
        onClick(url);
    };

    const cards = categories.map(category => {
        return (
            <StyledWrapper
                key={category.id}
                {...styles.categoryCard?.container}
                data-test-selector={`category_${category.name || category.shortDescription}`}
            >
                <Clickable
                    onClick={() =>
                        categoryClickHandler(category.path || category.urlSegment, category.path || category.urlSegment)
                    }
                    // href={category.path || category.urlSegment}
                    {...styles.categoryCard?.imageClickable}
                >
                    <LazyImage
                        src={category.smallImagePath}
                        altText={category.imageAltText || category.shortDescription}
                        {...styles.categoryCard?.image}
                    />
                </Clickable>
                <Link
                    onClick={() =>
                        categoryClickHandler(category.path || category.urlSegment, category.path || category.urlSegment)
                    }
                    data-test-selector={`categoryName_${category.name || category.shortDescription}`}
                    {...styles.categoryCard?.titleLink}
                    aria-label={category.shortDescription}
                >
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
        );
    });

    return (
        <StyledWrapper data-test-selector="ShopyByCategories">
            {fields.sectionTitle && (
                <TitleTypography
                    linkText={translate("View All Categories")}
                    link={categoryPageLink?.url}
                    data-test-selector="shopByCategory-title"
                >
                    {fields.sectionTitle}
                </TitleTypography>
            )}
            <StyledWrapper data-test-selector="slides" {...styles.carouselSlidesContainer}>
                <XngCarousel
                    scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                    navigation={true}
                    spaceBetween={28}
                    shadowNavigation={true}
                    simulateTouch={true}
                    extendedStyles={styles.categoryCarousel}
                >
                    {cards}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategories),
    definition: {
        group: "Xngage Custom" as any,
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                displayName: "Section Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "General",
            },
            {
                name: fields.selectedCategoryIds,
                editorTemplate: "CategoriesField",
                defaultValue: [],
                displayName: "Categories to Display",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
